import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $profileFeatureStore } from '@lib/store';
import { useAccountFlow, useCurrencySymbol } from '@shared/hooks';
import type { AccountByIdResponse } from '@shared/api';
import { SidebarItem, SidebarRow } from '@widgets';
import { Button, Icon, Text } from '@shared/widgets';
import SettingIcon from '@assets/icons/setting.svg';
import NotificationIcon from '@assets/icons/notification.svg';

interface IAccountSidebarItemsProps {
  account?: AccountByIdResponse;
  openEditModal: () => void;
  openChangeAlertModal: () => void;
  openEditRecipientsModal: () => void;
}

export const AccountSidebarItems = (props: IAccountSidebarItemsProps) => {
  const { id } = useParams();

  const profileFeatureStore = useUnit($profileFeatureStore.store);

  const { t } = useTranslation('account');

  const accountFlow = useAccountFlow(profileFeatureStore.data?.accountFlows, id ? parseInt(id) : undefined);
  const symbol = useCurrencySymbol(props.account?.currency);

  if (!props.account) return null;
  return (
    <>
      <SidebarItem title={t('sidebar.accountDetails.title')} icon={<Icon icon={<SettingIcon />} color='inherit' />}>
        <SidebarRow
          leftRowText={t('sidebar.accountDetails.fields.accountOwner')}
          rightRowText={props.account.name || t('sidebar.accountDetails.fields.none')}
        />
        <SidebarRow
          leftRowText={t('sidebar.accountDetails.fields.accountLabel')}
          rightRowText={props.account.accountLabel || t('sidebar.accountDetails.fields.none')}
        />
        <SidebarRow
          leftRowText={t('sidebar.accountDetails.fields.costCentre')}
          rightRowText={props.account.costCentre || t('sidebar.accountDetails.fields.none')}
        />
        <SidebarRow
          leftRowText={t('sidebar.accountDetails.fields.accountCategory')}
          rightRowText={props.account.category || t('sidebar.accountDetails.fields.none')}
        />
        {(accountFlow?.editAccountCategory || accountFlow?.editAccountLabel || accountFlow?.editCostCentre) && (
          <Button color='outlined' className='mb-5' fullWidth onClick={props.openEditModal}>
            {t('buttons.editDetails', { ns: 'common' })}
          </Button>
        )}
      </SidebarItem>
      <SidebarItem
        title={t('sidebar.alerts.title')}
        icon={<Icon icon={<NotificationIcon />} color='inherit' />}
        isHidden={!(accountFlow?.accountAlertsSettings.minimalBalance || accountFlow?.accountAlertsSettings.declinedAuths)}
      >
        <SidebarRow
          leftRowText={t('sidebar.alerts.fields.minimalAvailableBalance')}
          isHidden={!accountFlow?.accountAlertsSettings.minimalBalance}
          rightRowText={
            props.account.minimalBalanceAlertSubscription?.minimalBalance
              ? `${symbol}${props.account.minimalBalanceAlertSubscription?.minimalBalance}`
              : t('sidebar.alerts.fields.none')
          }
        />
        {props.account.minimalBalanceAlertSubscription?.recipientDetails?.map((item, idx) => (
          <SidebarRow
            key={idx}
            isHidden={
              !(
                accountFlow?.accountAlertsSettings.minimalBalance &&
                !!props.account?.minimalBalanceAlertSubscription?.recipientDetails?.some((row) => row.email != null || row.firstName != null)
              )
            }
            leftRowText={item.firstName}
            rightRowText={item.email}
          />
        ))}
        {accountFlow?.accountAlertsSettings.minimalBalance && (
          <Button color='outlined' className='mb-5' fullWidth onClick={props.openChangeAlertModal}>
            {t('sidebar.alerts.buttons.changeAlert')}
          </Button>
        )}
        <Text className='mb-5' size='sm'>
          {t('sidebar.alerts.buttons.declinedAuthorizations')}
        </Text>
        {props.account.declinedAuthorizationSubscription?.recipientDetails?.map((item, idx) => (
          <SidebarRow
            key={idx}
            isHidden={!accountFlow?.accountAlertsSettings.minimalBalance}
            leftRowText={item.firstName}
            rightRowText={item.email}
          />
        ))}
        {accountFlow?.accountAlertsSettings.declinedAuths && (
          <Button color='outlined' className='mb-5' fullWidth onClick={props.openEditRecipientsModal}>
            {t('sidebar.alerts.buttons.editRecipients')}
          </Button>
        )}
      </SidebarItem>
    </>
  );
};
